import Decimal from 'decimal.js';
import { CURRENCY } from './constants/index.js';
/**
 * Single source of truth for monetary arithmetic.
 *
 * Rules (do not duplicate these anywhere else):
 *  - money is never a float; it is a Decimal internally and a `numeric(12,2)` string at rest
 *  - quantities keep 3 decimals (kg), prices keep 4 (unit prices can be fractional)
 *  - every rounding is HALF_UP to 2 decimals, applied once per line and once per VAT group
 */
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });
export const MONEY_SCALE = 2;
export const QUANTITY_SCALE = 3;
export const PRICE_SCALE = 4;
export function dec(value) {
    return value instanceof Decimal ? value : new Decimal(value);
}
/** Rounds to 2 decimals, HALF_UP. The only rounding function in the codebase. */
export function roundMoney(value) {
    return dec(value).toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);
}
/** Serialises to the exact string shape `numeric(12,2)` expects. */
export function moneyToString(value) {
    return roundMoney(value).toFixed(MONEY_SCALE);
}
export function quantityToString(value) {
    return dec(value).toDecimalPlaces(QUANTITY_SCALE, Decimal.ROUND_HALF_UP).toFixed(QUANTITY_SCALE);
}
export function priceToString(value) {
    return dec(value).toDecimalPlaces(PRICE_SCALE, Decimal.ROUND_HALF_UP).toFixed(PRICE_SCALE);
}
/** Net line total, VAT excluded (Italian wholesale convention: prices are netto). */
export function lineNet(quantity, unitPrice) {
    return roundMoney(dec(quantity).times(dec(unitPrice)));
}
/** VAT amount for a net base at the given percentage rate (e.g. 22 for 22%). */
export function vatAmount(net, vatRate) {
    return roundMoney(dec(net).times(dec(vatRate)).dividedBy(100));
}
export function sum(values) {
    return values.reduce((acc, v) => acc.plus(dec(v)), new Decimal(0));
}
/**
 * Document totals with per-rate VAT breakdown. VAT is computed once per rate
 * group on the summed net, not per line, so rounding cannot drift.
 */
export function documentTotals(lines) {
    const netByRate = new Map();
    for (const line of lines) {
        const signed = lineNet(line.quantity, line.unitPrice);
        const delta = line.kind === 'return' ? signed.negated() : signed;
        netByRate.set(line.vatRate, (netByRate.get(line.vatRate) ?? new Decimal(0)).plus(delta));
    }
    const vatGroups = [...netByRate.entries()]
        .sort(([a], [b]) => a - b)
        .map(([vatRate, rawNet]) => {
        const net = roundMoney(rawNet);
        return { vatRate, net, vat: vatAmount(net, vatRate) };
    });
    const net = roundMoney(sum(vatGroups.map((g) => g.net)));
    const vat = roundMoney(sum(vatGroups.map((g) => g.vat)));
    return { net, vat, gross: roundMoney(net.plus(vat)), vatGroups };
}
export function formatMoney(value, locale) {
    return new Intl.NumberFormat(locale === 'it' ? 'it-IT' : 'tr-TR', {
        style: 'currency',
        currency: CURRENCY,
    }).format(roundMoney(value).toNumber());
}
//# sourceMappingURL=money.js.map